import { FC } from "react";
import { Extends } from "@common/helpers/typescript";
import { useUpdateUserMutation } from "@api/user.api";
import { FormikProvider, useFormik } from "formik";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { InviteProfile } from "../steps/InviteProfile";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { ProductPreview } from "../components/ProductPreview";
import { OnboardingStepKey } from "../steps";
import { useManageSteps } from "../useManageSteps";
import { InviteOutro } from "../steps/Outro/InviteOutro";
import {
    InviteUserProfileFlowFormSchema,
    inviteUserProfileFormSchema,
} from "../lib";

type InviteFlowSteps = Extends<OnboardingStepKey, "inviteProfile" | "outro">;

interface InviteFlowProps {
    onFinish: () => void;
}

export const InviteFlow: FC<InviteFlowProps> = ({ onFinish }) => {
    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<InviteFlowSteps>(true, () => "inviteProfile");

    const updateUserMutation = useUpdateUserMutation();

    const formik = useFormik<InviteUserProfileFlowFormSchema>({
        initialValues: {
            firstName: "",
            lastName: "",
        },
        validate: validateFormikWithZodSchema(inviteUserProfileFormSchema),
        onSubmit: submitHelper({
            handler: async ({ firstName, lastName }) => {
                await updateUserMutation.mutateAsync({
                    preferredName: `${firstName} ${lastName}`,
                });

                go("outro");
            },
        }),
    });

    let main: React.ReactNode;
    let sidebar: React.ReactNode;

    switch (currentStep) {
        case "inviteProfile":
            main = <InviteProfile busy={formik.isSubmitting} />;
            sidebar = <ProductPreview />;
            break;
        case "outro":
            main = <InviteOutro onFinished={onFinish} />;
            break;
    }

    return (
        <FormikProvider value={formik}>
            <OnboardingLayout
                currentStep={currentStep}
                nextStep={nextStep}
                isExiting={isExiting}
                main={main}
                sidebar={sidebar}
            />
        </FormikProvider>
    );
};
